import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { G4sLayout } from '../../components/gestion4sat/G4sLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  MapPin, Users, CheckCircle2, Clock, User, RefreshCw,
  Navigation, Building2, Car, AlertTriangle
} from 'lucide-react';
import { getMapaOperativo } from '../../lib/gestion4satApi';
import { toast } from 'sonner';

function fmt(n) { return (n || 0).toLocaleString('es-CL'); }

// Leaflet icon fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const TECH_COLORS = ['#3b82f6', '#ef4444', '#8b5cf6', '#f59e0b', '#06b6d4', '#ec4899', '#14b8a6', '#f97316'];

function createTechIcon(color) {
  return new L.DivIcon({
    className: '',
    html: `<div style="width:34px;height:34px;border-radius:50%;background:${color};border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center">
      <svg width="16" height="16" fill="white" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
    </div>`,
    iconSize: [34, 34], iconAnchor: [17, 17], popupAnchor: [0, -20],
  });
}

function createOrderIcon(color, completed) {
  const opacity = completed ? 0.5 : 1;
  return new L.DivIcon({
    className: '',
    html: `<div style="width:22px;height:22px;border-radius:4px;background:${color};border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.2);opacity:${opacity}"></div>`,
    iconSize: [22, 22], iconAnchor: [11, 11], popupAnchor: [0, -14],
  });
}

const tipoLabels = {
  instalacion: 'Instalaci\u00f3n', mantencion: 'Mantenci\u00f3n', asistencia: 'Asistencia',
  revision: 'Revisi\u00f3n', retiro: 'Retiro', otro: 'Otro',
};

function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 0) {
      map.fitBounds(L.latLngBounds(positions), { padding: [50, 50], maxZoom: 13 });
    }
  }, [map, positions]);
  return null;
}

export default function G4sMapaAdminPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getMapaOperativo({ fecha });
      setData(d);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [fecha]);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const allPositions = [];
  if (data) {
    data.tecnicos.forEach(t => {
      if (t.coordenadas?.lat) allPositions.push([t.coordenadas.lat, t.coordenadas.lng]);
      t.ordenes.forEach(o => {
        if (o.coordenadas_destino?.lat) allPositions.push([o.coordenadas_destino.lat, o.coordenadas_destino.lng]);
      });
    });
  }

  return (
    <G4sLayout>
      <div className="space-y-4" data-testid="g4s-mapa-admin">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Mapa Operativo</h1>
            <p className="text-sm text-muted-foreground">Ubicaci&oacute;n de t&eacute;cnicos y progreso de &oacute;rdenes en tiempo real</p>
          </div>
          <div className="flex items-center gap-2">
            <Input type="date" value={fecha} onChange={e => setFecha(e.target.value)} className="w-40" />
            <Button variant="outline" size="icon" onClick={load} disabled={loading}><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
          </div>
        </div>

        {/* KPIs */}
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card><CardContent className="p-4 flex items-center gap-3">
              <Users className="h-5 w-5 text-blue-500" />
              <div>
                <div className="text-xl font-black tabular-nums">{data.total_tecnicos}</div>
                <div className="text-xs text-muted-foreground">T&eacute;cnicos activos</div>
              </div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <Clock className="h-5 w-5 text-amber-500" />
              <div>
                <div className="text-xl font-black tabular-nums">{data.total_ordenes - data.total_completadas}</div>
                <div className="text-xs text-muted-foreground">Pendientes</div>
              </div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <div>
                <div className="text-xl font-black tabular-nums">{data.total_completadas}/{data.total_ordenes}</div>
                <div className="text-xs text-muted-foreground">Completadas</div>
              </div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <div className="text-xs text-muted-foreground">Auto-refresh cada 30s</div>
            </CardContent></Card>
          </div>
        )}

        {/* Map */}
        {loading && !data ? <Skeleton className="h-[450px] w-full rounded-xl" /> : (
          <div className="rounded-xl overflow-hidden border" style={{ height: '450px' }}>
            <MapContainer center={[-33.45, -70.66]} zoom={11} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {allPositions.length > 1 && <FitBounds positions={allPositions} />}

              {data?.tecnicos.map((tech, idx) => {
                const color = TECH_COLORS[idx % TECH_COLORS.length];
                const techIcon = createTechIcon(color);
                const minutesAgo = tech.ultima_actualizacion
                  ? Math.round((Date.now() - new Date(tech.ultima_actualizacion).getTime()) / 60000)
                  : null;

                return (
                  <React.Fragment key={tech.tecnico_id}>
                    {/* Tech location */}
                    {tech.coordenadas?.lat && (
                      <Marker position={[tech.coordenadas.lat, tech.coordenadas.lng]} icon={techIcon}>
                        <Popup>
                          <div className="text-xs space-y-1 min-w-[160px]">
                            <div className="font-bold flex items-center gap-1">{tech.tecnico_nombre}</div>
                            <div>{tech.ordenes_completadas}/{tech.ordenes_total} completadas</div>
                            {minutesAgo !== null && (
                              <div className="text-gray-500">Hace {minutesAgo < 60 ? `${minutesAgo} min` : `${Math.round(minutesAgo / 60)}h`}</div>
                            )}
                          </div>
                        </Popup>
                      </Marker>
                    )}

                    {/* Tech's orders */}
                    {tech.ordenes.map(o => {
                      if (!o.coordenadas_destino?.lat) return null;
                      const completed = o.estado === 'completada';
                      return (
                        <Marker key={o.id} position={[o.coordenadas_destino.lat, o.coordenadas_destino.lng]} icon={createOrderIcon(color, completed)}>
                          <Popup>
                            <div className="text-xs space-y-1 min-w-[160px]">
                              <div className="font-bold">{tipoLabels[o.tipo] || o.tipo} {completed && '\u2705'}</div>
                              <div>{o.empresa_razon_social}</div>
                              {o.patente_formateada && <div className="font-mono">{o.patente_formateada}</div>}
                              {o.direccion && <div className="text-gray-500">{o.direccion}</div>}
                              <div style={{ color }}>{tech.tecnico_nombre}</div>
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </MapContainer>
          </div>
        )}

        {/* Technician cards */}
        {data?.tecnicos.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.tecnicos.map((tech, idx) => {
              const color = TECH_COLORS[idx % TECH_COLORS.length];
              const pct = tech.ordenes_total > 0 ? Math.round((tech.ordenes_completadas / tech.ordenes_total) * 100) : 0;
              return (
                <Card key={tech.tecnico_id}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
                      <span className="font-medium text-sm">{tech.tecnico_nombre}</span>
                      {tech.coordenadas && <MapPin className="h-3 w-3 text-green-500 ml-auto" />}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-mono tabular-nums">{tech.ordenes_completadas}/{tech.ordenes_total}</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {tech.ordenes.filter(o => o.estado !== 'completada').map(o => (
                        <Badge key={o.id} variant="outline" className="text-[10px] gap-0.5">
                          {o.patente_formateada || tipoLabels[o.tipo]}
                          {o.prioridad === 'alta' && <AlertTriangle className="h-2.5 w-2.5 text-red-500" />}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </G4sLayout>
  );
}
